import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Redirect, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import BackLink from '../shared/BackLink';
import CategoryHeader from './CategoryHeader';
import CategoryProduct from './CategoryProduct';
import cartUtils from '../../../utils/cart';
import { colorModeStyle } from '../../../utils/contrastColor';
import {
  catalogLayoutSelector,
  anyColorSetSelector,
  selectCategoryRedirectPath,
} from '../../../selectors';
import { doesCategoryContainsMetallicProducts } from '../../../utils/misc';
import {
  isShippingRestrictedForParent,
  isProductAllowed,
  isCategoryRestricted as computeCategoryRestricted,
} from '../helpers/internationalRestriction';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 50%; // 1:2 Aspect Ratio

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    object-fit: contain;
  }
`;

const ContentList = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`;

// Status badges shared with Offerings (kept inline rather than extracted —
// only two call sites today). If a third surface appears, lift to a shared
// component module.
const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
`;

const RestrictedBadge = styled(Badge)`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
`;

const RestrictedWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0.55;
  pointer-events: none;
  cursor: not-allowed;
`;

const Category = (props) => {
  const { match, history } = props;
  const categoryId = match.params.id;

  const [category, setCategory] = useState(null);
  const [productId, setProductId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNthFree, setIsNthFree] = useState(false);
  const [
    categoryContainsMetallicProducts,
    setCategoryContainsMetallicProducts,
  ] = useState(false);
  const [metallicProductsFilterActive, setMetallicProductsFilterActive] =
    useState(false);

  const availablePoses = useSelector(
    (state) => state.additionalData.availablePoses,
  );
  const selectedStudent = useSelector(
    (state) => state.additionalData.selectedStudent,
  );
  const shootProducts = useSelector((state) => state.shoot.shootProducts);
  const catalogLayout = useSelector(catalogLayoutSelector);
  const defaultPaperType = useSelector(
    (state) => state.shoot?.shoot?.pricing_model?.default_paper_type,
  );
  const orders = useSelector((state) => state.v3Order.orders);
  const shootEventId = useSelector((state) => {
    const s = state.shoot.shoot;
    return s?.event_id || s?.id;
  });
  const parent = useSelector((state) => state.parent);
  const shootData = useSelector((state) => state.shoot.shoot);
  const restricted = isShippingRestrictedForParent(shootData, parent);

  const anyColorSet = useSelector(anyColorSetSelector);
  const redirectPath = useSelector((state) =>
    selectCategoryRedirectPath(state, categoryId),
  );

  useEffect(() => {
    let categoryToUpdate;

    // If categoryId is 'nth-free', find the nth-free category
    if (categoryId === 'nth-free') {
      categoryToUpdate = shootProducts.find(
        (cat) => cat.category_type === 'nth_free',
      );
      setIsNthFree(true);
    } else {
      // Find the category that matches the id in the url
      categoryToUpdate = shootProducts.find(
        (cat) => cat.id.toString() === categoryId,
      );
    }

    if (categoryToUpdate?.products?.[0]?.id) {
      setProductId(categoryToUpdate.products[0].id);
    }

    if (doesCategoryContainsMetallicProducts(categoryToUpdate?.products)) {
      setCategoryContainsMetallicProducts(true);

      if (defaultPaperType === 'metallic') {
        setMetallicProductsFilterActive(true);
      }
    }

    setCategory(categoryToUpdate);

    if (categoryId === 'nth-free') {
      Swal.fire(
        'Congratulations, you qualify for a free item!',
        'Please configure your free item',
      );
    }

    setIsLoading(false);
  }, [props.hasPoses, productId, categoryId]);

  // Decided during render, never from the effect above (ENG-1683): see
  // selectCategoryRedirectPath.
  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  // TODO: v3 - add a proper loading spinner here
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // get array of unlocked groups for lockable products
  const orderKey =
    shootEventId && selectedStudent?.id
      ? `${shootEventId}-${selectedStudent.id}`
      : null;
  const cartItems = (orderKey ? orders[orderKey] : undefined)?.cartItems || [];
  const unlockedGroups = cartUtils.unlockedGroups(cartItems);
  // For nth free products, use lowest qualifier price (equal or lesser value rule)
  const lowestPriceNthFreeQualifier =
    cartUtils.lowestPriceNthFreeQualifier(cartItems);
  // Keep highest for backward compatibility if needed elsewhere
  const highestPriceNthFreeQualifier =
    cartUtils.highestPriceNthFreeQualifier(cartItems);

  let childCategories = [];

  // If the current category has child categories, find them
  if (
    category &&
    category.child_category_ids &&
    category.child_category_ids.length > 0
  ) {
    childCategories = shootProducts.filter((potentialChildCategory) =>
      category.child_category_ids.includes(potentialChildCategory.id),
    );
  }

  // Tracks position among the cards that render with .bnl-product-regular (i.e.
  // everything except the double-column hero), to drive the alternating
  // checkerboard-white background — the styling itself lives in CategoryProduct.
  let regularCardCount = 0;

  const categoryProducts = category?.products
    .filter((product) => {
      // Apply the nth_free_value filter only if isNthFree is true
      // Use lowest qualifier price for "equal or lesser value" rule
      if (isNthFree && product.nth_free_value > highestPriceNthFreeQualifier) {
        return false;
      }

      // Filter child_products from displaying within the category itself
      // (needed for multi_config products)
      if (product.product_type === 'child_product') {
        return false;
      }

      // Hide shippable products when international shipping is blocked for this parent
      if (!isProductAllowed(product, restricted)) {
        return false;
      }

      // If the metallic product filter is active, only include products that are metallic
      if (metallicProductsFilterActive) {
        return product.is_metallic === true;
      }
      // Otherwise, exclude products that are metallic
      return product.is_metallic !== true;
    })
    .map((product) => {
      // A card renders as .bnl-product-regular unless it's the double-column
      // hero (mirrors CategoryProduct's className). The checkerboard highlights
      // the 1st and 4th of each group of four such cards, and is suppressed in
      // color mode where each card carries its own inline background.
      const isRegularCard =
        catalogLayout === 'single' || product.display_type !== 'display_full';
      const checkerWhite =
        !anyColorSet &&
        isRegularCard &&
        ((regularCardCount + 1) % 4 === 1 || (regularCardCount + 1) % 4 === 0);
      if (isRegularCard) regularCardCount += 1;

      return (
        <CategoryProduct
          key={product.id}
          product={product}
          unlockedGroups={unlockedGroups}
          isNthFree={isNthFree}
          checkerWhite={checkerWhite}
        />
      );
    });

  const childCategoryProductsDisplay = childCategories.map((subCategory) => {
    const product = subCategory?.products?.[0];
    const subRestricted = computeCategoryRestricted(
      subCategory,
      shootProducts,
      restricted,
    );
    const baseClass = subRestricted ? '' : 'cursor-pointer';
    // Single-column layout stacks subcategory cards full-width; double-column
    // keeps the half-width grid. (Layout applies only on this category page —
    // the /offers page is intentionally unaffected.)
    const className =
      catalogLayout === 'single'
        ? `bnl-product-group bnl-product-group--full col-xs-12 col-md-12 col-lg-12 ${baseClass}`
        : `bnl-product-group bnl-product-group--alternate col-xs-12 col-md-6 col-lg-6 ${baseClass}`;

    // Color mode: apply the subcategory's color (or white default) with
    // contrast-correct text and a matching border, mirroring Offerings. No
    // inline style otherwise.
    const subColorStyle = anyColorSet
      ? colorModeStyle(subCategory.color)
      : undefined;

    const subCardBody = (
      <>
        {!subRestricted && (
          <Link
            to={`/v3/categories/${subCategory.id}`}
            className="categoryLink"
          />
        )}
        <h3 className="bnl-product-group__header">
          {subCategory.name}
          {subCategory.featured_text ? ` - ${subCategory.featured_text}` : ''}
        </h3>
        <div className="bnl-product-group__subheader">
          {subCategory.subheader}
        </div>
        <div className="bnl-product-group__image-container">
          <ImageContainer>
            <img
              className="bnl-product-group__image--single"
              src={subCategory.image_url}
            />
          </ImageContainer>
        </div>
        {product?.is_multi_config === true && (
          <>
            <p className="bnl-product-regular__desc">{product?.description}</p>

            <ContentList>
              {product?.contents?.map((content, index) => (
                <p key={`${product.id}-${content}-${index}`}>{content}</p>
              ))}
            </ContentList>
          </>
        )}
      </>
    );

    return (
      <li key={subCategory.id} className={className} style={subColorStyle}>
        {restricted && subRestricted && (
          <RestrictedBadge>Not available internationally</RestrictedBadge>
        )}
        {subRestricted ? (
          <RestrictedWrap>{subCardBody}</RestrictedWrap>
        ) : (
          subCardBody
        )}
      </li>
    );
  });

  // If the current category has child categories, display them
  if (childCategories.length > 0) {
    return (
      <div className="bnl-product-groups-container">
        <BackLink />
        <div className="clearfix">
          <h2
            className="bnl-product-groups__header"
            style={{ paddingTop: 0, marginTop: 0 }}
          >
            {category.name}
          </h2>
        </div>
        <ul className="bnl-product-groups row">
          {childCategoryProductsDisplay}
        </ul>
      </div>
    );
  }

  // Render the products for the current category
  return (
    <div className="bnl-product-groups-container">
      {!isNthFree && <BackLink />}
      <CategoryHeader
        key={category?.id}
        isNthFree={isNthFree}
        categoryName={category?.name}
        categoryContainsMetallicProducts={categoryContainsMetallicProducts}
        highestPriceNthFreeQualifier={highestPriceNthFreeQualifier}
        metallicProductsFilterActive={metallicProductsFilterActive}
        setMetallicProductsFilterActive={setMetallicProductsFilterActive}
      />
      <ul className="bnl-product-groups row">{categoryProducts}</ul>
    </div>
  );
};

export default withRouter(Category);
